import { z } from 'zod';

/**
 * Retail Media RM-3: öffentliche Ausspielung unter /v1/rm/ (Shop-JavaScript, Theme App Extension, Plugins).
 *
 * Der Shop bekommt IDs, ein signiertes Token und das Label – nie fertiges HTML. Gerendert wird mit den Produktkarten
 * des Shops; das Label kommt aus der Antwort und muss immer angezeigt werden. Feldnamen in snake_case, weil die
 * Schnittstelle für fremde Shopsysteme gedacht ist.
 */

/** Label bezahlter Positionen – kommt aus der API, der Shop darf es nicht wegkonfigurieren (Wortlaut vor RM-6 anwaltlich prüfen) */
export const ANZEIGE_LABEL = 'Anzeige';

/** Boost-Faktor β für neue Marktplätze (Migration 0500); bestehende behielten 0 – 0 schaltet den Ad-Boost aus */
export const BOOST_BETA_STANDARD = 0.35;

export const SCHLUESSEL_ARTEN = {
  oeffentlich: 'Öffentlich (Shop-JavaScript)',
  geheim: 'Geheim (Server des Marktplatzes)',
} as const;
export type SchluesselArt = keyof typeof SCHLUESSEL_ARTEN;

export const AUSSPIELUNG_GRENZEN = {
  /** höchstens so viele Treffer je Anfrage */
  anzahlMax: 12,
  /** so lange darf der Shop eine Antwort zwischenspeichern (Sekunden) */
  ttl: 60,
  /** harter Abbruch – danach leere Trefferliste statt Fehler */
  timeoutMs: 80,
  /** Gültigkeit eines Tokens für Impression und Klick */
  tokenMinuten: 30,
} as const;

const Text = (max: number) => z.string().trim().max(max).nullish().transform((v) => v || null);

export const AusspielungAnfrageSchema = z.object({
  platzierung: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/),
  kontext: z.object({
    /** Kategorie bzw. Kollektion der Seite (Handle oder Name) */
    kollektion: Text(200),
    kategorie: Text(200),
    suche: Text(200),
    /** Produkt der Seite (eigene ID oder Shop-ID) – wird selbst nicht beworben */
    produkt_id: Text(100),
  }).default({}),
  anzahl: z.number().int().min(1).max(AUSSPIELUNG_GRENZEN.anzahlMax).default(4),
  /** anonymisierte Sitzung des Besuchers (Zuordnung der Bestellungen, RM-4) */
  session: z.string().trim().min(8).max(100),
  /** Produkte, die der Shop ohnehin zeigt – werden nicht zusätzlich beworben */
  ausschliessen: z.array(z.string().max(100)).max(200).default([]),
});
export type AusspielungAnfrage = z.infer<typeof AusspielungAnfrageSchema>;

/**
 * Format eines Treffers (RM-9): Sponsored Products rendert der Shop mit seinen Produktkarten, Markenbanner und
 * Display-Banner rendert das SDK selbst aus `werbemittel`. Offsite kommt nie in die Ausspielung im Shop.
 */
export type AusspielungFormat = 'sponsored_product' | 'sponsored_brand' | 'display';
export const BANNER_FORMATE = ['sponsored_brand', 'display'] as const satisfies readonly AusspielungFormat[];

/** Produkt eines Markenbanners (bis zu drei) – eigenes Token je Produkt für Klick und Zuordnung der Bestellung */
/**
 * Darstellung eines beworbenen Produkts für Shops ohne Shopify: Titel, Bild, Preis und Adresse der Produktseite.
 * null, wenn der Katalog keine Produktadresse hat (dann rendert der Shop selbst oder Shopify über den Handle).
 */
export interface AusspielungProduktAnsicht {
  titel: string;
  bild: string | null;
  preis_cent: number | null;
  waehrung: string;
  url: string;
}

export interface AusspielungBannerProdukt {
  produkt_id: string;
  shop_produkt_id: string | null;
  handle: string | null;
  produkt?: AusspielungProduktAnsicht | null;
  token: string;
}

/** Werbemittel eines Bannerformats – Bilder als absolute URLs, Ziel als https-Adresse oder Pfad im Shop */
export interface AusspielungWerbemittel {
  id: string;
  art: 'marke' | 'display';
  headline: string;
  text: string;
  cta: string;
  ziel_url: string;
  logo: string | null;
  bild_desktop: string | null;
  bild_mobil: string | null;
  /** nur Markenbanner: Produkte, die im Shop stehen */
  produkte: AusspielungBannerProdukt[];
}

export interface AusspielungTreffer {
  format: AusspielungFormat;
  /** gebuchte WKZ-Platzierung (Festpreis, ohne Auktion) – ein Klick darauf kostet nichts */
  wkz: boolean;
  /** beworbenes Produkt – null bei Bannerformaten */
  produkt_id: string | null;
  /** Produkt im Shop (Shopify-ID) und Handle, falls übertragen */
  shop_produkt_id: string | null;
  handle: string | null;
  /** Angebot des Händlers (Variante) im Marktplatz */
  angebot_id: string | null;
  /** Darstellung ohne Shopify (Titel, Bild, Preis, Adresse) – nur Sponsored Products */
  produkt?: AusspielungProduktAnsicht | null;
  /** nur Bannerformate */
  werbemittel?: AusspielungWerbemittel;
  token: string;
  label: string;
}

export interface AusspielungAntwort {
  treffer: AusspielungTreffer[];
  ttl: number;
}

/** Kennzahlen einer WKZ-Buchung (Impressionen und Klicks, nie berechnet) – gesamt und je Tag (Europe/Berlin) */
export interface WkzKennzahl {
  buchungId: string;
  impressionen: number;
  klicks: number;
  tage: { tag: string; impressionen: number; klicks: number }[];
}

/** Hinweis am Anbieternamen, wenn die Buy Box über den Ad-Boost gewonnen wurde (Wortlaut vor RM-6 anwaltlich prüfen) */
export const BUYBOX_LABEL = 'Gesponsertes Angebot';

/** Buy Box mit Ad-Boost (RM-5) – vom Server des Marktplatzes mit geheimem Schlüssel; Preise in Cent */
export const BuyBoxAnfrageSchema = z.object({
  /** Artikel (Modell, EAN-Gruppe oder Shop-Produkt) – Schlüssel für Protokoll und Rotation */
  produkt_id: z.string().trim().min(1).max(100),
  angebote: z.array(z.object({
    angebot_id: z.string().trim().min(1).max(100),
    /** eigenes Produkt des Händlers, falls bekannt (sonst wird angebot_id als Produkt oder Variante gesucht) */
    produkt_id: z.string().trim().max(100).nullish(),
    preis_cent: z.number().int().min(0),
    bestand: z.number().int().nullable(),
    liefertage: z.number().min(0).max(90).nullable(),
    bewertung: z.number().min(0).max(5).nullable(),
  })).min(1).max(50),
  session: z.string().trim().min(8).max(100).optional(),
});
export type BuyBoxAnfrage = z.infer<typeof BuyBoxAnfrageSchema>;

export interface BuyBoxAntwort {
  gewinner: string;
  bezahlt: boolean;
  /** nur bei bezahlt – am Anbieternamen anzeigen */
  label: string | null;
  /** nur bei bezahlt – für Impression und Klick wie bei der Ausspielung */
  token: string | null;
}

/**
 * Buy Box im Marktplatz-Shop (RM-5): der Block auf der Produktseite fragt mit dem öffentlichen Schlüssel, ob ein
 * Angebot die Buy Box über den Ad-Boost gewinnt. Die Angebote kennt das Modul selbst (Sammelprodukt des Marktplatzes).
 */
export const ShopBuyBoxAnfrageSchema = z.object({
  /** Shopify-Produkt (Ziffern oder gid://shopify/Product/…) */
  shop_produkt_id: z.string().trim().min(1).max(100),
  session: z.string().trim().min(8).max(100),
});

export interface ShopBuyBoxGruppe {
  /** Gruppe (EAN bzw. Merkmalsschlüssel) wie im Metafeld dxl_marketplace.offers */
  gruppe: string;
  angebot_id: string;
  /** Variante im Shop, die vorausgewählt wird */
  variant_id: string | null;
  label: string;
  token: string;
}

/** Nur Gruppen, in denen der Ad-Boost entscheidet – sonst gilt der Gewinner aus dem Metafeld */
export interface ShopBuyBoxAntwort {
  gruppen: ShopBuyBoxGruppe[];
  ttl: number;
}

/** Impression oder Klick (RM-4) – Token aus der Ausspielung; Schlüssel im Inhalt, weil sendBeacon keine Köpfe setzt */
export const EreignisAnfrageSchema = z.object({
  token: z.string().min(20).max(2_000),
  typ: z.enum(['impression', 'klick']),
  schluessel: z.string().max(100).optional(),
});
export type EreignisAnfrage = z.infer<typeof EreignisAnfrageSchema>;

/* ------------------------------------------------------------------ Schlüssel im Konto */

export const SchluesselAnlegenSchema = z.object({
  art: z.enum(['oeffentlich', 'geheim']),
  name: z.string().trim().max(80, 'Höchstens 80 Zeichen').default(''),
});
export type SchluesselAnlegen = z.infer<typeof SchluesselAnlegenSchema>;

export interface WerbungSchluessel {
  id: string;
  art: SchluesselArt;
  name: string;
  /** erste Zeichen zum Wiedererkennen, z. B. „rm_pub_Ab12…“ */
  anfang: string;
  erstelltAm: string;
  widerrufenAm: string | null;
}

/** Nur direkt nach dem Anlegen: der vollständige Schlüssel (wird nicht gespeichert) */
export interface WerbungSchluesselNeu extends WerbungSchluessel {
  schluessel: string;
}

/* ------------------------------------------------------------------ Einbindung und Einstellungen im Konto */

/** Warenkorb-Attribut mit der anonymen Sitzung – darüber werden Bestellungen den Klicks zugeordnet (RM-4) */
export const RM_SITZUNG_ATTRIBUT = '_dxl_rm_sitzung';

export const ATTRIBUTION_GRENZEN = { min: 1, max: 30, standard: 14 } as const;

export const WerbungEinstellungenSchema = z.object({
  attributionTage: z.number({ invalid_type_error: 'Bitte eine Zahl angeben' }).int('Bitte ganze Tage angeben')
    .min(ATTRIBUTION_GRENZEN.min, `Mindestens ${ATTRIBUTION_GRENZEN.min} Tag`)
    .max(ATTRIBUTION_GRENZEN.max, `Höchstens ${ATTRIBUTION_GRENZEN.max} Tage`),
});
export type WerbungEinstellungenEingabe = z.infer<typeof WerbungEinstellungenSchema>;

/* ------------------------------------------------------------------ Ad-Boost in der Buy Box (RM-5) */

/** Grenzen wie die Checks in Migration 0440: Leitplanken sind einstellbar, aber nie abschaltbar */
export const BOOST_GRENZEN = {
  preisfensterProzent: { min: 0, max: 20 },
  liefertageMehr: { min: 0, max: 5 },
  minBewertung: { min: 0, max: 5 },
  rotationProzent: { min: 10, max: 90 },
} as const;

/** Leitplanken ohne eigene Einstellung (Spalten-Standard in Migration 0440) */
export const BOOST_LEITPLANKEN_STANDARD = { preisfensterProzent: 5, liefertageMehr: 2, minBewertung: 0, rotationProzent: 70 } as const;

const zahl = () => z.number({ invalid_type_error: 'Bitte eine Zahl angeben' });
const nachkomma = (n: number, stellen: number) => Math.abs(n * 10 ** stellen - Math.round(n * 10 ** stellen)) < 1e-9;

export const AdBoostEinstellungSchema = z.object({
  beta: zahl().min(0, 'Mindestens 0').max(1, 'Höchstens 1').refine((n) => nachkomma(n, 2), 'Höchstens zwei Nachkommastellen'),
  preisfensterProzent: zahl().min(0, 'Mindestens 0 %').max(BOOST_GRENZEN.preisfensterProzent.max, `Höchstens ${BOOST_GRENZEN.preisfensterProzent.max} %`)
    .refine((n) => nachkomma(n, 1), 'Höchstens eine Nachkommastelle'),
  liefertageMehr: zahl().int('Bitte ganze Tage angeben').min(0, 'Mindestens 0 Tage').max(BOOST_GRENZEN.liefertageMehr.max, `Höchstens ${BOOST_GRENZEN.liefertageMehr.max} Tage`),
  minBewertung: zahl().min(0, 'Mindestens 0 Sterne').max(5, 'Höchstens 5 Sterne').refine((n) => nachkomma(n, 2), 'Höchstens zwei Nachkommastellen'),
  rotationProzent: zahl().int('Bitte ganze Prozent angeben')
    .min(BOOST_GRENZEN.rotationProzent.min, `Mindestens ${BOOST_GRENZEN.rotationProzent.min} %`)
    .max(BOOST_GRENZEN.rotationProzent.max, `Höchstens ${BOOST_GRENZEN.rotationProzent.max} %`),
});
export type AdBoostEinstellung = z.infer<typeof AdBoostEinstellungSchema>;

/** Angaben für die Einbindung im Shop */
export interface WerbungEinbindung {
  /** öffentliche Adresse der Plattform (PUBLIC_URL) – Basis für /v1/rm/… */
  basisUrl: string;
  sitzungAttribut: string;
}
